import { Component, ElementRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

// Composants
import { BaseComponent } from 'src/app/@shared/components/abstract/base.component';

// Services
import { MessageServiceBroker } from 'src/app/@core/services/message.service';

/** Cible d'un renvoi interne cliqué dans le corps d'un article. */
export interface DocLinkTarget {
    id: string;
    anchor?: string;
}

/**
 * Le clic demande au navigateur d'ouvrir ailleurs — Ctrl (ou Cmd), Maj, Alt, ou
 * un bouton autre que le principal, dont le clic du milieu. Un lien porteur
 * d'une vraie adresse sait alors quoi faire tout seul : l'application doit
 * s'effacer, et surtout ne pas appeler `preventDefault()` (#984).
 */
export function opensInNewTab(event: MouseEvent): boolean {
    return event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0;
}

/** Longueur de l'extrait joint à une référence copiée, en caractères. */
const EXCERPT_LENGTH = 160;

/**
 * Corps d'un article du manuel.
 *
 * Le fragment vient du build (`assets/docs/<pays>/articles/<id>.html`) : c'est
 * notre propre production, jamais une saisie utilisateur, d'où le passage par
 * `bypassSecurityTrustHtml` — la sanitisation d'Angular retirerait les
 * maquettes, qui sont du HTML structuré avec styles inline.
 *
 * Deux comportements sont portés ici parce qu'ils ne peuvent pas l'être dans le
 * fragment lui-même :
 * - les renvois internes (`a.doc-link`) reçoivent l'adresse de leur article
 *   dans l'application, puis sont interceptés au clic simple pour naviguer sans
 *   recharger ; les ancres nues (`href="#ancre"`) le sont aussi, comme des
 *   renvois vers une section de l'article courant ;
 * - le bloc « Impact comptable » est masqué quand le dossier n'a pas la
 *   comptabilité — il reste toujours visible sur le site public ;
 * - les intertitres reçoivent, pour l'équipe Biifor seule, le bouton qui copie
 *   la référence de leur section.
 *
 * C'est aussi lui qui réclame la feuille de style du manuel : elle doit suivre
 * le fragment partout où il s'affiche — la page comme la fenêtre d'aide, qui
 * s'ouvre depuis n'importe quel écran sans être jamais passée par la
 * documentation.
 */
@Component({
    selector: 'app-documentation-content',
    imports: [],
    templateUrl: './documentation-content.component.html',
    styleUrl: './documentation-content.component.scss'
})
export class DocumentationContentComponent extends BaseComponent {

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    @Output() navigate = new EventEmitter<DocLinkTarget>();

    private _host = inject(ElementRef);

    private _messageService = inject(MessageServiceBroker);

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    /** Identifiant de l'article affiché : la clé des références copiées. */
    private _articleId = '';
    public get articleId(): string {
        return this._articleId;
    }
    @Input() public set articleId(value: string) {
        this._articleId = value ?? '';
    }

    /** Corps resserré : la fenêtre d'aide, qui n'a ni plan ni rail autour d'elle. */
    private _dense = false;
    public get dense(): boolean {
        return this._dense;
    }
    @Input() public set dense(value: boolean) {
        this._dense = value;
    }

    private _html = '';
    public get html(): string {
        return this._html;
    }
    @Input() public set html(value: string) {
        this._html = value ?? '';
        this._safeHtml = this._sanitizer.bypassSecurityTrustHtml(this._html);
        this._resolveLinks();
        this._decorate();
    }

    private _safeHtml: SafeHtml = '';
    public get safeHtml(): SafeHtml {
        return this._safeHtml;
    }

    private _sanitizer = inject(DomSanitizer);

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    /** Le dossier a la comptabilité : sinon, les blocs « Impact comptable » sont masqués. */
    public get showComptable(): boolean {
        return !!this.appService.selectedClient?.hasFeature(this.Features.Accounting);
    }

    /**
     * Le copier-référence est un outil de rédaction — il sert à ouvrir un ticket
     * de correction sur un passage du manuel, pas à lire. Réservé à l'équipe.
     */
    public get staff(): boolean {
        return !!this.appService.user?.isStaff;
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    constructor() {
        super();
        this.documentationService.loadKit();
    }

    /**
     * Un seul écouteur pour tout le fragment : les éléments qu'on y injecte
     * n'ont donc pas d'écouteur propre, et rien n'est à détacher quand le corps
     * de l'article est remplacé.
     */
    public onClickContent(event: MouseEvent): void {
        const target = event.target as HTMLElement | null;

        const button = target?.closest?.('.doc-copyref') as HTMLElement | null;
        if (button) {
            event.preventDefault();
            this._copyReference(button.dataset['docAnchor'] ?? '');
            return;
        }

        const link = target?.closest?.('a.doc-link, a[data-doc-id], a[href^="#"]') as HTMLElement | null;
        if (!link || opensInNewTab(event)) {
            return;
        }
        event.preventDefault();
        this._followLink(link);
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /** L'adresse d'un article **dans l'application**, dossier compris. */
    private _articleUrl(id: string, anchor?: string): string {
        return this.router.serializeUrl(
            this.router.createUrlTree([this.appService.clientUrl('/documentation'), id], anchor ? { fragment: anchor } : {})
        );
    }

    /**
     * La référence désigne l'article par son **identifiant**, jamais par son
     * slug : un article publié dans les deux pays a deux adresses et un seul
     * identifiant, et c'est déjà la clé de tous les renvois du manuel.
     */
    private _copyReference(anchor: string): void {
        const heading = anchor ? this._root()?.querySelector(`[id="${anchor}"]`) as HTMLElement | null : null;
        if (!heading) {
            return;
        }
        const reference = `[doc:${this._articleId}#${anchor}] « ${this._excerpt(heading)} »`;
        navigator.clipboard.writeText(reference).then(() => {
            this._messageService.sendMessage({
                severity: 'success',
                summary: 'Copié',
                detail: 'La référence de cette section a été copiée dans le presse-papier.',
                life: 2000
            });
        });
    }

    /**
     * Le bouton est posé dans le DOM plutôt que rendu par Angular : le corps de
     * l'article est du HTML injecté, où aucune liaison de gabarit ne s'applique.
     * Attendu d'un cycle, le temps que `[innerHTML]` ait remplacé le fragment.
     */
    private _decorate(): void {
        if (!this.staff) {
            return;
        }
        setTimeout(() => {
            this._root()?.querySelectorAll('h2[id], h3[id]').forEach((heading) => {
                if (heading.querySelector('.doc-copyref')) {
                    return;
                }
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'doc-copyref';
                button.dataset['docAnchor'] = heading.id;
                button.title = 'Copier la référence de cette section';
                button.innerHTML = '<i class="pi pi-copy"></i>';
                heading.appendChild(button);
            });
        }, 1);
    }

    /**
     * De quoi reconnaître le passage visé dans un ticket : le premier paragraphe
     * de la section, coupé sur un mot. À défaut — une section qui ouvre sur une
     * maquette ou un tableau — l'intertitre lui-même.
     */
    private _excerpt(heading: HTMLElement): string {
        let text = '';
        let element = heading.nextElementSibling;
        while (element && !/^H[1-6]$/.test(element.tagName)) {
            const paragraph = element.tagName === 'P' ? element : element.querySelector('p');
            if (paragraph?.textContent?.trim()) {
                text = paragraph.textContent.trim();
                break;
            }
            element = element.nextElementSibling;
        }
        if (!text) {
            text = heading.textContent?.trim() ?? '';
        }
        text = text.replace(/\s+/g, ' ');
        if (text.length <= EXCERPT_LENGTH) {
            return text;
        }
        const cut = text.slice(0, EXCERPT_LENGTH);
        return `${cut.slice(0, cut.lastIndexOf(' '))}…`;
    }

    /**
     * Deux écritures pour un même geste, et l'ancre nue est la plus fragile
     * des deux : `<base href="/">` résout une URL réduite à un fragment contre
     * la racine de l'application, si bien qu'un `href="#ancre"` laissé au
     * navigateur emmène le lecteur au tableau de bord (#983). Le site public,
     * lui, la sert nativement. Elle désigne donc ici une section de l'article
     * courant, et se suit comme un renvoi vers cet article-là.
     */
    private _followLink(link: HTMLElement): void {
        const href = link.getAttribute('href') ?? '';
        const anchor = link.dataset['docAnchor'] || (href.startsWith('#') ? href.slice(1) : '');
        // Un `href="#"` sans ancre ni article — le lien décoratif d'une maquette
        // — ne désigne rien : le clic est avalé, et il ne s'ensuit rien.
        const id = link.dataset['docId'] || (anchor ? this._articleId : '');
        if (!id) {
            return;
        }
        this.navigate.emit({ id, anchor: anchor || undefined });
    }

    /**
     * Le fragment sort du build avec un `href="#"` : il ignore le dossier, qui
     * est dans l'adresse, et jusqu'au pays du lecteur. Cette adresse de
     * remplacement est posée ici, une fois le fragment rendu — c'est ce qui rend
     * au renvoi son statut de **vrai lien** : Ctrl+clic, clic du milieu et
     * « Ouvrir dans un nouvel onglet » du menu contextuel deviennent l'affaire
     * du navigateur, sans une ligne pour les traiter (#984).
     *
     * L'identifiant et l'ancre sont recopiés dans le jeu de données au passage :
     * une fois l'adresse réécrite, le lien n'est plus reconnaissable à son
     * `href`, et c'est le jeu de données que lit le clic simple.
     */
    private _resolveLinks(): void {
        setTimeout(() => {
            this._root()?.querySelectorAll('a.doc-link, a[href^="#"]').forEach((element) => {
                const link = element as HTMLAnchorElement;
                const href = link.getAttribute('href') ?? '';
                const anchor = link.dataset['docAnchor'] || (href.startsWith('#') ? href.slice(1) : '');
                const id = link.dataset['docId'] || (anchor ? this._articleId : '');
                if (!id) {
                    return;
                }
                link.dataset['docId'] = id;
                if (anchor) {
                    link.dataset['docAnchor'] = anchor;
                }
                link.setAttribute('href', this._articleUrl(id, anchor));
            });
        }, 1);
    }

    /** Le fragment rendu, racine de toute lecture du DOM de l'article. */
    private _root(): HTMLElement | null {
        return this._host.nativeElement.querySelector('.doc-scope');
    }
}
